import { formatDateDisplay, formatSSNInput } from "@/lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Users, Plus, Search, Download, Shield, ChevronUp, ChevronDown, ChevronsUpDown } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { EligibilityBadge } from "@/components/ui/eligibility-badge";
import { DueDateCountdown } from "@/components/ui/due-date-countdown";
import { useEffect, useMemo, useState, ChangeEvent } from "react";
import { useLocation } from "wouter";
import type { Employee } from "@shared/schema";
import { insertEmployeeSchema } from "@shared/schema";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { tabWithErrors, validatePersonalStep } from "@/lib/addEmployeeSteps";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { exportEmployeesToCSV } from "@/lib/exportUtils";
import { EmployeeImport } from "@/components/employees/EmployeeImport";
import { EMPLOYMENT_TYPE_OPTIONS_SHORT, formatEmploymentType } from "@/lib/employmentType";
import { EMPLOYEE_STATUS_OPTIONS, formatStatus, normalizeStatus } from "@/lib/employeeStatus";
import { AddressFields } from "@/components/forms/AddressFields";
import { EmployeeStatusSelect } from "@/components/forms/EmployeeStatusSelect";
import { composeAddressLine, emptyAddressFields } from "@/lib/address";
import { sortEmployeesByFullName } from "@/lib/employeeSort";
import { COMPENSATION_TYPE_OPTIONS, PAY_CYCLE_OPTIONS } from "@/lib/employeeDefaults";
import { EMPLOYMENT_TYPES } from "@/lib/usStates";
import { OrganizationSelect } from "@/components/forms/OrganizationSelect";
import { formatPhoneInputValue } from "@/lib/phone";

export default function Employees() {
  const [searchTerm, setSearchTerm] = useState("");
  const [isEmployeeDialogOpen, setIsEmployeeDialogOpen] = useState(false);
  const [addEmployeeTab, setAddEmployeeTab] = useState<string>("personal");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [departmentFilter, setDepartmentFilter] = useState<string>("all");
  // ONB-14: support deep-link from employer dashboard to filter on employees
  // whose onboarding is not yet complete. Reads ?filter=onboarding-incomplete
  // from the URL once at mount.
  const [onboardingIncompleteFilter, setOnboardingIncompleteFilter] = useState(() => {
    if (typeof window === "undefined") return false;
    return new URLSearchParams(window.location.search).get("filter") === "onboarding-incomplete";
  });
  const [employmentTypeMissingFilter, setEmploymentTypeMissingFilter] = useState(() => {
    if (typeof window === "undefined") return false;
    return new URLSearchParams(window.location.search).get("filter") === "employment-type-missing";
  });
  const [showEligibility, setShowEligibility] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState<string>("");
  const [, setLocation] = useLocation();
  
  // Sorting state
  type SortField = 'name' | 'email' | 'department' | 'position' | 'status' | 'hireDate';
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <ChevronsUpDown className="h-4 w-4 ml-1 text-gray-400" />;
    }
    return sortDirection === 'asc' 
      ? <ChevronUp className="h-4 w-4 ml-1" /> 
      : <ChevronDown className="h-4 w-4 ml-1" />;
  };
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Export employees to CSV
  const handleExport = () => {
    exportEmployeesToCSV(sortedEmployees);
  };

  // Phone formatting helper
  const handlePhoneFieldChange = (
    event: ChangeEvent<HTMLInputElement>,
    onChange: (value: string) => void
  ) => {
    const formatted = formatPhoneInputValue(event.target.value);
    onChange(formatted);
  };

  // Get authenticated user to read clientId for scoping
  const { data: user } = useQuery<any>({ queryKey: ["/api/auth/user"] });

  // Form setup for adding employees
  const employeeForm = useForm({
    resolver: zodResolver(insertEmployeeSchema),
    defaultValues: {
      clientId: user?.clientId || 0,
      firstName: "",
      lastName: "",
      email: "",
      personalEmail: "",
      phone: "",
      workPhone: "",
      dateOfBirth: "",
      sex: "",
      ssn: "",
      hireDate: "",
      department: "",
      position: "",
      employmentType: "",
      salary: "",
      status: "active",
      scheduledHours: "",
      compensationType: "",
      payCycle: "",
      departmentId: null,
      jobTitleId: null,
      locationId: null,
      employeeClassId: null,
      ...emptyAddressFields(),
      emergencyContact: "",
      emergencyPhone: "",
    },
  });

  // Update form default values when user data loads
  useEffect(() => {
    if (user?.clientId) {
      employeeForm.setValue("clientId", user.clientId);
    }
  }, [user?.clientId, employeeForm]);

  // Create employee mutation
  const createEmployeeMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await apiRequest("POST", `/api/employees`, data);
      return response.json();
    },
    onSuccess: (result: any) => {
      if (user?.clientId) {
        queryClient.invalidateQueries({
          queryKey: ["/api/employees"],
        });
      }
      employeeForm.reset();
      setAddEmployeeTab("personal");
      setIsEmployeeDialogOpen(false);
      toast({
        title: "Employee added",
        description:
          "Account created — send login from the Logins tab when ready.",
        variant: "success",
      });
    },
    onError: (error) => {
      console.error("Employee creation error:", error);
      toast({ title: "Failed to add employee", variant: "destructive" });
    },
  });

  // Client-scoped Employees (backend filtered)
  const { data: employees = [] } = useQuery<Employee[]>({
    queryKey: ["/api/employees"],
    enabled: !!user?.clientId,
  });

  // Fetch bulk eligibility data for all employees
  const { data: eligibilityData } = useQuery({
    queryKey: ["employer-employees-eligibility", user?.clientId],
    queryFn: async () => {
      const response = await fetch(`/api/clients/${user?.clientId}/employees/eligibility`);
      if (!response.ok) return null;
      return response.json();
    },
    enabled: !!user?.clientId && employees.length > 0,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Fetch client plans for the eligibility plan selector
  const { data: clientPlans = [] } = useQuery<any[]>({
    queryKey: ["/api/client-plans", user?.clientId],
    queryFn: async () => {
      const response = await fetch(`/api/client-plans?clientId=${user?.clientId}`);
      if (!response.ok) return [];
      return response.json();
    },
    enabled: !!user?.clientId,
  });


  // Create a map of employee ID to eligibility info for quick lookup
  const eligibilityByEmployee = useMemo(() => {
    const map = new Map<number, any>();
    if (eligibilityData?.employees) {
      for (const emp of eligibilityData.employees) {
        map.set(emp.employeeId, emp);
      }
    }
    return map;
  }, [eligibilityData]);

  // Auto-select first plan when eligibility is enabled
  useEffect(() => {
    if (showEligibility && !selectedPlanId && clientPlans.length > 0) {
      const firstPlan = clientPlans[0]?.plan || clientPlans[0];
      if (firstPlan?.id) {
        setSelectedPlanId(String(firstPlan.id));
      }
    }
  }, [showEligibility, selectedPlanId, clientPlans]);

  // Helper to get eligibility for the selected plan
  const getEmployeeEligibility = (employeeId: number) => {
    const empEligibility = eligibilityByEmployee.get(employeeId);
    if (!empEligibility || !selectedPlanId) return null;

    return empEligibility.plans?.find(
      (p: any) => String(p.planId) === selectedPlanId
    ) || null;
  };

  // Backend already filters by client; use as-is
  const scopedEmployees = Array.isArray(employees) ? employees : [];

  const sortedEmployees = useMemo(() => {
    return sortEmployeesByFullName(scopedEmployees);
  }, [scopedEmployees]);

  const filteredEmployees = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    const normalizedStatusFilter =
      statusFilter === "all" ? null : normalizeStatus(statusFilter);
    const normalizedDepartmentFilter =
      departmentFilter === "all" ? null : departmentFilter;

    const employeeMatchesSearch = (employee: Employee) => {
      if (normalizedSearch.length === 0) {
        return true;
      }

      const fullName = `${employee.firstName ?? ""} ${
        employee.lastName ?? ""
      }`.toLowerCase();
      const email = (employee.email ?? "").toLowerCase();
      const department = (employee.department ?? "").toLowerCase();

      return (
        fullName.includes(normalizedSearch) ||
        email.includes(normalizedSearch) ||
        department.includes(normalizedSearch)
      );
    };

    const employeeMatchesStatus = (employee: Employee) => {
      if (!normalizedStatusFilter) {
        return true;
      }

      const employeeStatusValue = normalizeStatus(employee.status);
      return employeeStatusValue === normalizedStatusFilter;
    };

    const employeeMatchesDepartment = (employee: Employee) => {
      if (!normalizedDepartmentFilter) {
        return true;
      }

      return employee.department === normalizedDepartmentFilter;
    };

    const employeeMatchesOnboardingFilter = (employee: Employee) => {
      if (!onboardingIncompleteFilter) return true;
      return (employee as any).onboardingCompletedAt == null;
    };

    const employeeMatchesEmploymentTypeFilter = (employee: Employee) => {
      if (!employmentTypeMissingFilter) return true;
      return !employee.employmentType || !String(employee.employmentType).trim();
    };

    const filtered = sortedEmployees.filter((employee) => {
      return (
        employeeMatchesSearch(employee) &&
        employeeMatchesStatus(employee) &&
        employeeMatchesDepartment(employee) &&
        employeeMatchesOnboardingFilter(employee) &&
        employeeMatchesEmploymentTypeFilter(employee)
      );
    });

    // Apply sorting if a sort field is selected
    if (!sortField) return filtered;

    return [...filtered].sort((a: any, b: any) => {
      let aValue: string;
      let bValue: string;

      switch (sortField) {
        case 'name':
          aValue = `${a.firstName || ''} ${a.lastName || ''}`.toLowerCase();
          bValue = `${b.firstName || ''} ${b.lastName || ''}`.toLowerCase();
          break;
        case 'email':
          aValue = (a.email || '').toLowerCase();
          bValue = (b.email || '').toLowerCase();
          break;
        case 'department':
          aValue = (a.department || '').toLowerCase();
          bValue = (b.department || '').toLowerCase();
          break;
        case 'position':
          aValue = (a.position || '').toLowerCase();
          bValue = (b.position || '').toLowerCase();
          break;
        case 'status':
          aValue = (a.status || '').toLowerCase();
          bValue = (b.status || '').toLowerCase();
          break;
        case 'hireDate':
          aValue = a.hireDate || '';
          bValue = b.hireDate || '';
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [
    sortedEmployees,
    searchTerm,
    statusFilter,
    departmentFilter,
    onboardingIncompleteFilter,
    sortField,
    sortDirection,
  ]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Employees</h1>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleExport}>
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
          {user?.clientId && (
            <EmployeeImport
              clientId={user.clientId}
              onImportComplete={() => queryClient.invalidateQueries({ queryKey: ["/api/employees"] })}
            />
          )}
          <Button size="sm" onClick={() => setIsEmployeeDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Employee
          </Button>
        </div>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex gap-3 items-center flex-wrap">
            <div className="relative w-64">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search employees..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {EMPLOYEE_STATUS_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All Departments" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Departments</SelectItem>
                {Array.from(
                  new Set(
                    employees
                      .map((emp) => emp.department)
                      .filter((dept): dept is string => !!dept && dept.trim() !== "")
                  )
                )
                  .sort()
                  .map((dept) => (
                    <SelectItem key={dept} value={dept}>
                      {dept}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
            {(statusFilter !== "all" || departmentFilter !== "all" || onboardingIncompleteFilter || employmentTypeMissingFilter) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setStatusFilter("all");
                  setDepartmentFilter("all");
                  setOnboardingIncompleteFilter(false);
                  setEmploymentTypeMissingFilter(false);
                  if (typeof window !== "undefined" && window.location.search) {
                    // Drop the ?filter= deep-link param so reloads don't re-apply.
                    setLocation("/employees");
                  }
                }}
                className="text-muted-foreground hover:text-foreground"
              >
                Clear filters
              </Button>
            )}
          </div>
          {onboardingIncompleteFilter && (
            <div className="mt-3 flex items-center gap-2">
              <Badge variant="secondary" className="gap-1">
                Onboarding: incomplete
                <button
                  type="button"
                  aria-label="Remove onboarding filter"
                  onClick={() => {
                    setOnboardingIncompleteFilter(false);
                    if (typeof window !== "undefined" && window.location.search) {
                      setLocation("/employees");
                    }
                  }}
                  className="ml-1 hover:text-foreground"
                >
                  ×
                </button>
              </Badge>
            </div>
          )}
          {employmentTypeMissingFilter && (
            <div className="mt-3 flex items-center gap-2">
              <Badge variant="secondary" className="gap-1">
                Employment type: missing
                <button
                  type="button"
                  aria-label="Remove employment type filter"
                  onClick={() => {
                    setEmploymentTypeMissingFilter(false);
                    if (typeof window !== "undefined" && window.location.search) {
                      setLocation("/employees");
                    }
                  }}
                  className="ml-1 hover:text-foreground"
                >
                  ×
                </button>
              </Badge>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Employee Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">
              Total Employees
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{sortedEmployees.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Active</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {
                sortedEmployees.filter(
                  (emp: Employee) => emp.status?.toLowerCase() === "active"
                ).length
              }
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">On Leave</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">
              {
                sortedEmployees.filter(
                  (emp: Employee) =>
                    emp.status?.toLowerCase() === "leave of absence" ||
                    emp.status?.toLowerCase() === "leave"
                ).length
              }
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Terminated</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              {
                sortedEmployees.filter(
                  (emp: Employee) => emp.status?.toLowerCase() === "terminated"
                ).length
              }
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Employee List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Employee Directory
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 select-none"
                    onClick={() => handleSort('name')}
                  >
                    <div className="flex items-center">
                      Employee Name
                      {getSortIcon('name')}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 select-none"
                    onClick={() => handleSort('email')}
                  >
                    <div className="flex items-center">
                      Email
                      {getSortIcon('email')}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 select-none"
                    onClick={() => handleSort('department')}
                  >
                    <div className="flex items-center">
                      Department
                      {getSortIcon('department')}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 select-none"
                    onClick={() => handleSort('position')}
                  >
                    <div className="flex items-center">
                      Position
                      {getSortIcon('position')}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 select-none"
                    onClick={() => handleSort('status')}
                  >
                    <div className="flex items-center">
                      Status
                      {getSortIcon('status')}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 select-none"
                    onClick={() => handleSort('hireDate')}
                  >
                    <div className="flex items-center">
                      Hire Date
                      {getSortIcon('hireDate')}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Type
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                {filteredEmployees.map((employee: any) => (
                  <tr key={employee.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-sm font-medium">
                            {employee.firstName?.[0] || ''}{employee.lastName?.[0] || ''}
                          </span>
                        </div>
                        <div>
                          <div 
                            className="font-medium text-gray-900 dark:text-gray-100 hover:text-blue-600 dark:hover:text-blue-400 cursor-pointer"
                            onClick={() => {
                              localStorage.setItem("userRole", "employer");
                              setLocation(`/employees/${employee.id}?role=employer`);
                            }}
                          >
                            {employee.firstName} {employee.lastName}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {employee.email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {employee.department || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {employee.position || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge
                          variant={
                            employee.status?.toLowerCase() === "active"
                              ? "default"
                              : employee.status?.toLowerCase() === "leave of absence" ||
                                employee.status?.toLowerCase() === "leave"
                              ? "secondary"
                              : "destructive"
                          }
                        >
                          {formatStatus(employee.status)}
                        </Badge>
                        {showEligibility && (() => {
                          const planEligibility = getEmployeeEligibility(employee.id);
                          if (!planEligibility) return <span className="text-gray-400 text-sm">-</span>;
                          const status = !planEligibility.isEligible
                            ? "ineligible"
                            : planEligibility.daysUntilEffective > 0
                              ? "pending"
                              : "eligible";
                          const label = !planEligibility.isEligible
                            ? "Ineligible"
                            : planEligibility.daysUntilEffective > 0
                              ? `In ${planEligibility.daysUntilEffective}d`
                              : "Eligible";
                          return (
                            <EligibilityBadge
                              status={status as any}
                              label={label}
                              tooltip={planEligibility.ineligibilityReason}
                              showIcon={true}
                            />
                          );
                        })()}
                        {showEligibility && (() => {
                          const planEligibility = getEmployeeEligibility(employee.id);
                          if (!planEligibility?.isEligible) return null;
                          return (
                            <span className="text-sm text-muted-foreground">
                              Eff: {planEligibility.effectiveDate || "-"}
                            </span>
                          );
                        })()}
                        {showEligibility && (() => {
                          const planEligibility = getEmployeeEligibility(employee.id);
                          if (!planEligibility || !planEligibility.isEligible) return null;

                          if (!planEligibility.acaCompliant) {
                            return (
                              <span
                                className="text-red-500 cursor-help text-sm"
                                title={planEligibility.acaWarning || `${planEligibility.totalWaitingDays} days exceeds ACA 90-day limit`}
                              >
                                ACA: {planEligibility.totalWaitingDays}
                              </span>
                            );
                          }
                          if (planEligibility.acaWarning) {
                            return (
                              <span
                                className="text-amber-500 cursor-help text-sm"
                                title={planEligibility.acaWarning}
                              >
                                ACA: {planEligibility.totalWaitingDays}
                              </span>
                            );
                          }
                          return (
                            <span
                              className="text-green-500 cursor-help text-sm"
                              title={`${planEligibility.totalWaitingDays} days total waiting period`}
                            >
                              ACA: {planEligibility.totalWaitingDays}
                            </span>
                          );
                        })()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {employee.hireDate
                        ? formatDateDisplay(employee.hireDate)
                        : "-"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {employee.employmentType || (
                        <Badge variant="destructive">Missing</Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredEmployees.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                No employees found matching your filters.
              </div>
            )}
          </div>
        </CardContent>
      </Card>


      {/* Add Employee Dialog */}
      <Dialog
        open={isEmployeeDialogOpen}
        onOpenChange={(open) => {
          setIsEmployeeDialogOpen(open);
          if (!open) setAddEmployeeTab("personal");
        }}
      >
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Employee</DialogTitle>
          </DialogHeader>
          <Form {...employeeForm}>
            <form
              onSubmit={employeeForm.handleSubmit((data) => {
                const employeePayload = {
                  ...data,
                  address: composeAddressLine(data),
                };
                createEmployeeMutation.mutate(employeePayload);
              }, (errors) => setAddEmployeeTab(tabWithErrors(errors)))}
              className="space-y-4"
            >
              <Tabs value={addEmployeeTab} onValueChange={setAddEmployeeTab} className="w-full">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="personal">Personal / Profile</TabsTrigger>
                  <TabsTrigger value="employment">Employment</TabsTrigger>
                </TabsList>

                {/* Personal Tab */}
                <TabsContent value="personal" className="space-y-4 mt-4">
                  {/* Row 1: Name */}
                  <div className="grid grid-cols-12 gap-4">
                    <FormField
                      control={employeeForm.control}
                      name="firstName"
                      render={({ field }) => (
                        <FormItem className="col-span-12 sm:col-span-6">
                          <FormLabel>First Name <span className="text-red-500">*</span></FormLabel>
                          <FormControl>
                            <Input placeholder="John" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={employeeForm.control}
                      name="lastName"
                      render={({ field }) => (
                        <FormItem className="col-span-12 sm:col-span-6">
                          <FormLabel>Last Name <span className="text-red-500">*</span></FormLabel>
                          <FormControl>
                            <Input placeholder="Doe" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* Row 2: Personal Email + Phone */}
                  <div className="grid grid-cols-12 gap-4">
                    <FormField
                      control={employeeForm.control}
                      name="personalEmail"
                      render={({ field }) => (
                        <FormItem className="col-span-12 sm:col-span-8">
                          <FormLabel>Personal Email</FormLabel>
                          <FormControl>
                            <Input
                              type="email"
                              placeholder="john.doe@gmail.com"
                              {...field}
                              value={field.value || ""}
                              onBlur={(e) =>
                                field.onChange(e.target.value.trim().toLowerCase())
                              }
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={employeeForm.control}
                      name="phone"
                      render={({ field }) => (
                        <FormItem className="col-span-12 sm:col-span-4">
                          <FormLabel>Phone</FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              value={field.value || ""}
                              placeholder="(555) 123-4567"
                              onChange={(e) => handlePhoneFieldChange(e, field.onChange)}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* Row 3: Date of Birth + Sex + SSN */}
                  <div className="grid grid-cols-12 gap-4">
                    <FormField
                      control={employeeForm.control}
                      name="dateOfBirth"
                      render={({ field }) => (
                        <FormItem className="col-span-12 sm:col-span-4">
                          <FormLabel>Date of Birth</FormLabel>
                          <FormControl>
                            <Input type="date" {...field} value={field.value || ""} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={employeeForm.control}
                      name="sex"
                      render={({ field }) => (
                        <FormItem className="col-span-12 sm:col-span-4">
                          <FormLabel>Sex</FormLabel>
                          <Select
                            value={field.value || ""}
                            onValueChange={field.onChange}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select sex" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="male">Male</SelectItem>
                              <SelectItem value="female">Female</SelectItem>
                              <SelectItem value="other">Other</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={employeeForm.control}
                      name="ssn"
                      render={({ field }) => (
                        <FormItem className="col-span-12 sm:col-span-4">
                          <FormLabel>SSN</FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              value={field.value || ""}
                              onChange={(e) => field.onChange(formatSSNInput(e.target.value))}
                              inputMode="numeric"
                              maxLength={11}
                              placeholder="123-45-6789"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* Address */}
                  <AddressFields control={employeeForm.control} />

                  {/* Cancel + Next Buttons */}
                  <div className="flex justify-end gap-2 pt-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setIsEmployeeDialogOpen(false);
                        setAddEmployeeTab("personal");
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      onClick={async () => {
                        if (await validatePersonalStep(employeeForm)) setAddEmployeeTab("employment");
                      }}
                    >
                      Next
                    </Button>
                  </div>
                </TabsContent>

                {/* Employment Tab */}
                <TabsContent value="employment" className="space-y-4 mt-4">
                  {/* Row 1: Work Email + Work Phone */}
                  <div className="grid grid-cols-12 gap-4">
                    <FormField
                      control={employeeForm.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem className="col-span-12 sm:col-span-8">
                          <FormLabel>Work Email <span className="text-red-500">*</span></FormLabel>
                          <FormControl>
                            <Input
                              type="email"
                              placeholder="john.doe@company.com"
                              {...field}
                              onBlur={(e) =>
                                field.onChange(e.target.value.trim().toLowerCase())
                              }
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={employeeForm.control}
                      name="workPhone"
                      render={({ field }) => (
                        <FormItem className="col-span-12 sm:col-span-4">
                          <FormLabel>Work Phone</FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              value={field.value || ""}
                              placeholder="(555) 123-4567"
                              onChange={(e) => {
                                const formatted = formatPhoneInputValue(e.target.value);
                                field.onChange(formatted);
                              }}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* Row 2: Department + Job Title */}
                  <div className="grid grid-cols-12 gap-4">
                    <FormField
                      control={employeeForm.control}
                      name="departmentId"
                      render={({ field }) => (
                        <FormItem className="col-span-12 sm:col-span-6">
                          <FormLabel>Department</FormLabel>
                          <FormControl>
                            <OrganizationSelect
                              orgType="department"
                              ownerType="client"
                              ownerId={user?.clientId}
                              value={field.value}
                              onChange={field.onChange}
                              placeholder="Select Department"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={employeeForm.control}
                      name="jobTitleId"
                      render={({ field }) => (
                        <FormItem className="col-span-12 sm:col-span-6">
                          <FormLabel>Job Title</FormLabel>
                          <FormControl>
                            <OrganizationSelect
                              orgType="job-title"
                              ownerType="client"
                              ownerId={user?.clientId}
                              value={field.value}
                              onChange={field.onChange}
                              placeholder="Select Job Title"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* Row 3: Location + Employee Class */}
                  <div className="grid grid-cols-12 gap-4">
                    <FormField
                      control={employeeForm.control}
                      name="locationId"
                      render={({ field }) => (
                        <FormItem className="col-span-12 sm:col-span-6">
                          <FormLabel>Location</FormLabel>
                          <FormControl>
                            <OrganizationSelect
                              orgType="location"
                              ownerType="client"
                              ownerId={user?.clientId}
                              value={field.value}
                              onChange={field.onChange}
                              placeholder="Select Location"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={employeeForm.control}
                      name="employeeClassId"
                      render={({ field }) => (
                        <FormItem className="col-span-12 sm:col-span-6">
                          <FormLabel>Employee Class</FormLabel>
                          <FormControl>
                            <OrganizationSelect
                              orgType="employee-class"
                              ownerType="client"
                              ownerId={user?.clientId}
                              value={field.value}
                              onChange={field.onChange}
                              placeholder="Select Employee Class"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* Row 4: Hire Date + Salary */}
                  <div className="grid grid-cols-12 gap-4">
                    <FormField
                      control={employeeForm.control}
                      name="hireDate"
                      render={({ field }) => (
                        <FormItem className="col-span-12 sm:col-span-6">
                          <FormLabel>Hire Date</FormLabel>
                          <FormControl>
                            <Input type="date" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={employeeForm.control}
                      name="salary"
                      render={({ field }) => (
                        <FormItem className="col-span-12 sm:col-span-6">
                          <FormLabel>Compensation amount</FormLabel>
                          <FormControl>
                            <Input placeholder="$65,000" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* Row 5: Scheduled Hours */}
                  <div className="grid grid-cols-12 gap-4">
                    <FormField
                      control={employeeForm.control}
                      name="scheduledHours"
                      render={({ field }) => (
                        <FormItem className="col-span-12 sm:col-span-6">
                          <FormLabel>Hours Worked (Weekly)</FormLabel>
                          <FormControl>
                            <Input placeholder="40" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* Row 6: Compensation Type + Pay Cycle */}
                  <div className="grid grid-cols-12 gap-4">
                    <FormField
                      control={employeeForm.control}
                      name="compensationType"
                      render={({ field }) => (
                        <FormItem className="col-span-12 sm:col-span-6">
                          <FormLabel>Compensation Type</FormLabel>
                          <Select
                            value={field.value || ""}
                            onValueChange={field.onChange}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select compensation type" />
                            </SelectTrigger>
                            <SelectContent>
                              {COMPENSATION_TYPE_OPTIONS.map((option) => (
                                <SelectItem key={option.value} value={option.value}>
                                  {option.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={employeeForm.control}
                      name="payCycle"
                      render={({ field }) => (
                        <FormItem className="col-span-12 sm:col-span-6">
                          <FormLabel>Pay Cycle</FormLabel>
                          <Select
                            value={field.value || ""}
                            onValueChange={field.onChange}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select pay cycle" />
                            </SelectTrigger>
                            <SelectContent>
                              {PAY_CYCLE_OPTIONS.map((option) => (
                                <SelectItem key={option.value} value={option.value}>
                                  {option.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* Row 7: Employment Type + Status */}
                  <div className="grid grid-cols-12 gap-4">
                    <FormField
                      control={employeeForm.control}
                      name="employmentType"
                      render={({ field }) => (
                        <FormItem className="col-span-12 sm:col-span-6">
                          <FormLabel>Employment Type</FormLabel>
                          <Select
                            value={field.value}
                            onValueChange={field.onChange}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select employment type" />
                            </SelectTrigger>
                            <SelectContent>
                              {EMPLOYMENT_TYPES.map((t) => (
                                <SelectItem key={t} value={t}>
                                  {t}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={employeeForm.control}
                      name="status"
                      render={({ field }) => (
                        <FormItem className="col-span-12 sm:col-span-6">
                          <FormLabel>Status <span className="text-red-500">*</span></FormLabel>
                          <EmployeeStatusSelect
                            value={field.value}
                            onChange={field.onChange}
                            placeholder="Select status"
                          />
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* Cancel + Add Employee Buttons */}
                  <div className="flex justify-end gap-2 pt-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setIsEmployeeDialogOpen(false);
                        setAddEmployeeTab("personal");
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={createEmployeeMutation.isPending}
                    >
                      {createEmployeeMutation.isPending ? "Adding..." : "Add Employee"}
                    </Button>
                  </div>
                </TabsContent>
              </Tabs>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* CSV Import — shared component renders its own button + dialogs */}
    </div>
  );
}
